/**
 * SignupPage - User registration with multi-field form and validation
 * 
 * Registration flow featuring:
 * - Personal info (First/Last name)
 * - Account info (Email, Password with strength indicator)
 * - Preferences (Sport interests, Skill level)
 * - Glassmorphism card design with neon cyan accents
 * - Real-time form validation
 * - Password strength visualization
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Check,
  Loader2,
  Trophy,
  Target,
  Zap,
  Flame,
  Waves,
  Bike,
  Dumbbell,
  Swords,
  Footprints,
  Flower2,
} from 'lucide-react';
import { useAuthStore } from '@/stores';
import { Heading, Text, Stack, Container, Divider } from '@/components/ui';
import styles from './SignupPage.module.css';

// =========================================
// SPORT CONFIGURATION
// =========================================
const SPORTS = [
  { id: 'basketball', label: 'Basketball', icon: Trophy },
  { id: 'soccer', label: 'Soccer', icon: Target },
  { id: 'tennis', label: 'Tennis', icon: Zap },
  { id: 'running', label: 'Running', icon: Flame },
  { id: 'swimming', label: 'Swimming', icon: Waves },
  { id: 'yoga', label: 'Yoga', icon: Flower2 },
];

const SKILL_LEVELS = [
  { id: 'beginner', label: 'Beginner', description: 'Just getting started' },
  { id: 'intermediate', label: 'Intermediate', description: 'Regular player' },
  { id: 'advanced', label: 'Advanced', description: 'Competitive level' },
];

// =========================================
// PASSWORD STRENGTH COMPONENT
// =========================================
function PasswordStrength({ password }) {
  const strength = useMemo(() => {
    if (!password) return { score: 0, label: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    
    score = Object.values(checks).filter(Boolean).length;
    
    const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['#FF3366', '#F59E0B', '#39FF14', '#00F0FF', '#00F0FF'];
    
    return {
      score,
      label: labels[score - 1] || '',
      color: colors[score - 1] || '',
      checks,
    };
  }, [password]);

  if (!password) return null;

  return (
    <div className={styles.passwordStrength}>
      <div className={styles.strengthBars}>
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className={`${styles.strengthBar} ${
              level <= strength.score ? styles.strengthBarActive : ''
            }`}
            style={{
              backgroundColor: level <= strength.score ? strength.color : undefined,
            }}
          />
        ))}
      </div>
      <Text size="xs" color="secondary" className={styles.strengthLabel}>
        {strength.label && (
          <span style={{ color: strength.color }}>{strength.label}</span>
        )}
      </Text>
    </div>
  );
}

// =========================================
// SPORT CHIP COMPONENT
// =========================================
function SportChip({ sport, selected, onClick, disabled }) {
  const Icon = sport.icon;
  
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${styles.sportChip} ${selected ? styles.sportChipSelected : ''}`}
      aria-pressed={selected}
    >
      <Icon size={16} className={styles.sportChipIcon} />
      <span className={styles.sportChipLabel}>{sport.label}</span>
      {selected && <Check size={12} className={styles.sportChipCheck} />}
    </button>
  );
}

// =========================================
// SKILL LEVEL SELECTOR COMPONENT
// =========================================
function SkillLevelSelector({ value, onChange, disabled }) {
  return (
    <div className={styles.skillSelector}>
      {SKILL_LEVELS.map((level) => (
        <button
          key={level.id}
          type="button"
          onClick={() => onChange(level.id)}
          disabled={disabled}
          className={`${styles.skillOption} ${
            value === level.id ? styles.skillOptionSelected : ''
          }`}
          aria-pressed={value === level.id}
        >
          <Text
            size="sm"
            weight="medium"
            className={styles.skillOptionLabel}
          >
            {level.label}
          </Text>
          <Text size="xs" color="muted" className={styles.skillOptionDesc}>
            {level.description}
          </Text>
        </button>
      ))}
    </div>
  );
}

// =========================================
// FORM PROGRESS COMPONENT
// =========================================
function FormProgress({ currentStep, totalSteps }) {
  return (
    <div className={styles.progressContainer}>
      <div className={styles.progressBar}>
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`${styles.progressStep} ${
              index < currentStep ? styles.progressStepActive : ''
            } ${index === currentStep ? styles.progressStepCurrent : ''}`}
          />
        ))}
      </div>
      <Text size="xs" color="muted" className={styles.progressText}>
        Step {currentStep + 1} of {totalSteps}
      </Text>
    </div>
  );
}

// =========================================
// PASSWORD INPUT WITH TOGGLE
// =========================================
function PasswordInput({
  value,
  onChange,
  error,
  placeholder,
  disabled,
  showStrength,
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className={styles.passwordWrapper}>
      <div className={styles.inputWrapper}>
        <div className={styles.inputIcon}>
          <Lock size={20} />
        </div>
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${styles.input} ${error ? styles.inputError : ''}`}
          aria-invalid={error ? 'true' : 'false'}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className={styles.passwordToggle}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {showStrength && <PasswordStrength password={value} />}
    </div>
  );
}

// =========================================
// MAIN SIGNUP PAGE COMPONENT
// =========================================
export function SignupPage() {
  const navigate = useNavigate();
  const { signup, isLoading: authLoading, error: authError } = useAuthStore();

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    sports: [],
    skillLevel: 'beginner',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Trigger fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Validation functions
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Personal Info validation
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      } else if (formData.firstName.trim().length < 2) {
        newErrors.firstName = 'First name must be at least 2 characters';
      }

      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      } else if (formData.lastName.trim().length < 2) {
        newErrors.lastName = 'Last name must be at least 2 characters';
      }
    }

    if (step === 1) {
      // Account Info validation
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else {
        // Check complexity
        const hasLowercase = /[a-z]/.test(formData.password);
        const hasUppercase = /[A-Z]/.test(formData.password);
        const hasNumber = /\d/.test(formData.password);
        if (!hasLowercase || !hasUppercase || !hasNumber) {
          newErrors.password = 'Password must include uppercase, lowercase, and number';
        }
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (step === 2) {
      // Preferences validation
      if (formData.sports.length === 0) {
        newErrors.sports = 'Please select at least one sport';
      }
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'You must accept the Terms of Service';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 2));
    }
  };

  // Handle previous step
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setErrors({});
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) return;

    setIsLoading(true);
    
    const userData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      displayName: `${formData.firstName} ${formData.lastName}`,
      sports: formData.sports,
      skillLevel: formData.skillLevel,
    };

    const success = await signup(userData);
    setIsLoading(false);

    if (success) {
      navigate('/discover');
    }
  };

  // Handle input changes
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle sport selection
  const toggleSport = (sportId) => {
    setFormData((prev) => {
      const sports = prev.sports.includes(sportId)
        ? prev.sports.filter((id) => id !== sportId)
        : [...prev.sports, sportId];
      return { ...prev, sports };
    });
    if (errors.sports) {
      setErrors((prev) => ({ ...prev, sports: undefined }));
    }
  };

  const isSubmitting = isLoading || authLoading;

  // Render form step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Stack direction="column" gap={5} fullWidth>
            <div className={styles.field}>
              <label htmlFor="firstName" className={styles.label}>
                First Name
              </label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <User size={20} />
                </div>
                <input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  placeholder="Enter your first name"
                  disabled={isSubmitting}
                  className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
                />
              </div>
              {errors.firstName && (
                <span className={styles.errorText} role="alert">
                  {errors.firstName}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="lastName" className={styles.label}>
                Last Name
              </label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <User size={20} />
                </div>
                <input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  placeholder="Enter your last name"
                  disabled={isSubmitting}
                  className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
                />
              </div>
              {errors.lastName && (
                <span className={styles.errorText} role="alert">
                  {errors.lastName}
                </span>
              )}
            </div>
          </Stack>
        );

      case 1:
        return (
          <Stack direction="column" gap={5} fullWidth>
            <div className={styles.field}>
              <label htmlFor="email" className={styles.label}>
                Email
              </label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <Mail size={20} />
                </div>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  placeholder="athlete@sportspots.com"
                  disabled={isSubmitting}
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                />
              </div>
              {errors.email && (
                <span className={styles.errorText} role="alert">
                  {errors.email}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <PasswordInput
                value={formData.password}
                onChange={handleChange('password')}
                placeholder="Create a strong password"
                disabled={isSubmitting}
                error={errors.password}
                showStrength
              />
              {errors.password && (
                <span className={styles.errorText} role="alert">
                  {errors.password}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Confirm Password
              </label>
              <PasswordInput
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                placeholder="Confirm your password"
                disabled={isSubmitting}
                error={errors.confirmPassword}
              />
              {errors.confirmPassword && (
                <span className={styles.errorText} role="alert">
                  {errors.confirmPassword}
                </span>
              )}
            </div>
          </Stack>
        );

      case 2:
        return (
          <Stack direction="column" gap={6} fullWidth>
            <div className={styles.field}>
              <label className={styles.label}>Sport Interests</label>
              <Text size="sm" color="secondary" className={styles.fieldHint}>
                Select the sports you&apos;re interested in
              </Text>
              <div className={styles.sportsGrid}>
                {SPORTS.map((sport) => (
                  <SportChip
                    key={sport.id}
                    sport={sport}
                    selected={formData.sports.includes(sport.id)}
                    onClick={() => toggleSport(sport.id)}
                    disabled={isSubmitting}
                  />
                ))}
              </div>
              {errors.sports && (
                <span className={styles.errorText} role="alert">
                  {errors.sports}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Skill Level</label>
              <SkillLevelSelector
                value={formData.skillLevel}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, skillLevel: value }))
                }
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.termsField}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.acceptTerms}
                  onChange={handleChange('acceptTerms')}
                  disabled={isSubmitting}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>
                  I agree to the{' '}
                  <Link to="/terms" className={styles.termsLink}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className={styles.termsLink}>
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.acceptTerms && (
                <span className={styles.errorText} role="alert">
                  {errors.acceptTerms}
                </span>
              )}
            </div>
          </Stack>
        );

      default:
        return null;
    }
  };

  // Step titles
  const stepTitles = ['Personal Info', 'Account Setup', 'Preferences'];
  const stepSubtitles = [
    'Tell us about yourself',
    'Create your login credentials',
    'Customize your experience',
  ];

  return (
    <div className={styles.page}>
      <Container size="sm" padding className={styles.container}>
        {/* Back Button */}
        <button
          type="button"
          onClick={currentStep === 0 ? () => navigate('/login') : handleBack}
          className={styles.backLink}
          aria-label={currentStep === 0 ? 'Go back to login' : 'Go back'}
        >
          <ArrowLeft size={24} />
        </button>

        {/* Glass Card */}
        <div
          className={`${styles.card} ${isVisible ? styles.cardVisible : ''} ${
            Object.keys(errors).length > 0 && isSubmitting ? styles.cardShake : ''
          }`}
        >
          <Stack direction="column" gap={6} align="center">
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.logo}>
                <span className={styles.logoIcon}>⚡</span>
              </div>
              <Heading level={1} size="h2" align="center" className={styles.title}>
                Create Account
              </Heading>
              <Text size="base" color="secondary" align="center">
                Join thousands of athletes
              </Text>
            </div>

            {/* Progress Indicator */}
            <FormProgress currentStep={currentStep} totalSteps={3} />

            {/* Step Title */}
            <div className={styles.stepHeader}>
              <Heading level={2} size="h4" align="center">
                {stepTitles[currentStep]}
              </Heading>
              <Text size="sm" color="secondary" align="center">
                {stepSubtitles[currentStep]}
              </Text>
            </div>

            {/* Form */}
            <form onSubmit={currentStep === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }} className={styles.form} noValidate>
              {renderStepContent()}

              {/* Auth Error */}
              {authError && (
                <div className={styles.authError} role="alert">
                  {authError}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className={styles.buttonGroup}>
                {currentStep < 2 ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={styles.submitButton}
                  >
                    <span>Continue</span>
                    <ArrowLeft size={20} className={styles.buttonArrow} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={styles.submitButton}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className={styles.spinner} />
                        <span>Creating Account...</span>
                      </>
                    ) : (
                      <span>Create Account</span>
                    )}
                  </button>
                )}
              </div>
            </form>

            {/* Footer */}
            <div className={styles.footer}>
              <Text size="sm" color="secondary" align="center">
                Already have an account?{' '}
                <Link to="/login" className={styles.loginLink}>
                  Sign In
                </Link>
              </Text>
            </div>
          </Stack>
        </div>
      </Container>
    </div>
  );
}

export default SignupPage;
